import { AxialRotationEnum, BoneTypeEnum, BridgeActionsBase, BridgeBase, BridgeMessages as BridgeMessagesBase, CaseModel, SelectedApexMech } from "@ortho-next/nextray-core";
import { ScrewList } from "../../app/core";
import { PlateOrientationType } from "../Models/AppModel";
import { BoneTypeMinEnum } from "../Models/Enums";
import { PlateModel } from "../Models/PlateModel";
import { Context } from "../States/Context";
import { CustomStandardAngles, OsteotomyCut, SetAxialRotation, SetAxialRotationSign, SetStandardAngleParams } from "../States/State";

/**
 * List of events that can be triggered by the angular part to interact with the three.js part.
 */
export interface BridgeActions extends BridgeActionsBase {
	/**
	 * Starts mechanical axis workflow in the active view.
	 */
	"mechanical-axes-btn": null;
	/**
	* Starts contralateral mechanical axis workflow in the active view.
	*/
	"ma-to-ctrlateral": null;
	/**
	 * Sets femur proximal as osteotomy site during insert osteotomy worflow.
	 */
	apexFemurProximal: null;
	/**
	 * Sets femur distal as osteotomy site during insert osteotomy worflow.
	 */
	apexFemurDistal: null;
	/**
	 * Sets tibia proximal as osteotomy site during insert osteotomy worflow.
	 */
	apexTibiaProximal: null;
	/**
	 * Sets tibia distal as osteotomy site during insert osteotomy worflow.
	 */
	apexTibiaDistal: null;
	/**
	 * Ends insert osteotomy workflow.
	 */
	"place-osteotomy": null;
	/**
	 * Sets LPFA standard angle to calculate apex.
	 */
	setStandardAngle_LPFA: number;
	/**
	 * Sets mLDFA standard angle to calculate apex.
	 */
	setStandardAngle_mLDFA: number;
	/**
	 * Sets MPTA standard angle to calculate apex.
	 */
	setStandardAngle_MPTA: number;
	/**
	 * Sets LDTA standard angle to calculate apex.
	 */
	setStandardAngle_LDTA: number;
	/**
	 * Sets PPTA standard angle to calculate apex.
	 */
	setStandardAngle_PPTA: number;
	/**
	 * Sets ADTA standard angle to calculate apex.
	 */
	setStandardAngle_ADTA: number;
	/**
	 * Adds or removes the plane cut along the osteotomy.
	 */
	cutEoC: boolean;
	/**
	 * Sets the closing correction for the EOC.
	 */
	osteotomyCutClosing: null;
	/**
	 * Sets the opening correction for the EOC.
	 */
	osteotomyCutOpening: null;
	/**
	 * Inserts plate to the scenes.
	 */
	insertPlate: PlateModel;
	/**
	 * Sets the femur axial rotation angle degrees.
	 */
	setFemurAxialRotation: number;
	/**
	 * Sets the femur axial rotation angle sign.
	 */
	setFemurAxialRotationSign: AxialRotationEnum;
	/**
	 * Sets the tibia axial rotation angle degrees.
	 */
	setTibiaAxialRotation: number;
	/**
	 * Sets the tibia axial rotation angle sign.
	 */
	setTibiaAxialRotationSign: AxialRotationEnum;
	/**
	 * Starts selecting apex workflow.
	 */
	startSelectingApex: null;
	/**
	 * Ends selecting apex workflow and restore old selected apex value.
	 */
	cancelSelectingApex: null;
	/**
	 * Switches the current state to calibrated.
	 */
	switchToCalibrated: null;
	/**
	 * Switches the current state to deformity.
	 */
	switchToDeformity: null;
	/**
	 * Switches the current state to EOC.
	 */
	switchToEOC: null;
	/**
	 * Switches the current state to templating.
	 */
	switchToTemplating: null;
	/**
	 * Switches the current state to RPM.
	 */
	switchToRPM: null;
	/**
	 * Starts the inserting plate workflow.
	 */
	startInsertingPlate: null;
	/**
	 * Ends inserting plate workflow.
	 */
	confirmPlate: null;
	/**
	 * Ends inserting plate workflow and restore old selected plate.
	 */
	cancelPlate: null;
	/**
	 * Flips the plate.
	 */
	flipPlate: PlateOrientationType;
	/**
	 * Generates images for print report.
	 */
	generateImagesToPrint: null;
	/**
	 * Updates measures available for print report only.
	 */
	updateMeausuresOnlyPrint: null;
	/**
	 * Starts the bone type selection workflow.
	 */
	startBoneTypeSelection: null;
	/**
	 * Ends bone type selection workflow without confirm.
	 */
	cancelBoneTypeSelection: null;
	/**
	 * Confirm bone type selection and ends workflow.
	 */
	confirmBoneTypeSelection: BoneTypeEnum;
	/**
	* Update screw list.
	*/
	updateScrews: ScrewList;

	/** LAYERS EVENTS **/

	/**
	 * Toggles the visibility of osteotomy layer
	 */
	setVisibility_osteotomy: boolean;
	/**
	 * Toggles the visibility of plate layer TODO check
	 */
	setVisibility_plate: boolean;
}

/**
 * Messages sent from three.js part to angular part.
 */
export interface BridgeMessages extends BridgeMessagesBase {
	/**
	 * Error during registering mechanical axis points.
	 */
	mechanicalAxisInsertError: never;
	/**
	 * Error during registering contralateral mechanical axis points.
	 */
	contralateralAxisInsertError: never;
	/**
	 * Error: osteotomy line (mechanical) is too long.
	 */
	osteotomyLengthError: never;
	/**
	 * Error: one of the two osteotomy doens't intersect the plane. Cannot cut the plane along osteotomy.
	 */
	cannotCut: never;
	/**
	 * AP plate loaded.
	 */
	plateLoadedAP: never;
	/**
	 * LT plate loaded.
	 */
	plateLoadedLT: never;
}

/**
 * Manages the communications between three.js and angular part.
 */
export class Bridge extends BridgeBase<BridgeActions, BridgeMessages> {
	protected declare _context: Context; // TODO put it in the package as generic

	public override mapEvent<K extends keyof BridgeActions>(type: K, args: BridgeActions[K]): void {
		switch (type) {
			case "mechanical-axes-btn":
				return this._context.handle("insertMechanicalAxis", this._model.activeView);
			case "ma-to-ctrlateral":
				return this._context.handle("insertControlaterMechanicalAxis", null);
			case "apexFemurProximal":
				return this._context.handle("selectApex", SelectedApexMech.femurProximal);
			case "apexFemurDistal":
				return this._context.handle("selectApex", SelectedApexMech.femurDistal);
			case "apexTibiaProximal":
				return this._context.handle("selectApex", SelectedApexMech.tibiaProximal);
			case "apexTibiaDistal":
				return this._context.handle("selectApex", SelectedApexMech.tibiaDistal);
			case "place-osteotomy":
				return this._context.handle("confirmApex", null);
			case "setStandardAngle_LPFA":
				return this._context.handle("setStandardAngle", { angle: CustomStandardAngles.LPFA, value: args as number } as SetStandardAngleParams);
			case "setStandardAngle_mLDFA":
				return this._context.handle("setStandardAngle", { angle: CustomStandardAngles.mLDFA, value: args as number } as SetStandardAngleParams);
			case "setStandardAngle_MPTA":
				return this._context.handle("setStandardAngle", { angle: CustomStandardAngles.MPTA, value: args as number } as SetStandardAngleParams);
			case "setStandardAngle_LDTA":
				return this._context.handle("setStandardAngle", { angle: CustomStandardAngles.LDTA, value: args as number } as SetStandardAngleParams);
			case "setStandardAngle_PPTA":
				return this._context.handle("setStandardAngle", { angle: CustomStandardAngles.PPTA, value: args as number } as SetStandardAngleParams);
			case "setStandardAngle_ADTA":
				return this._context.handle("setStandardAngle", { angle: CustomStandardAngles.ADTA, value: args as number } as SetStandardAngleParams);
			case "setVisibility_osteotomy":
				return this._context.handle("setVisibility", { key: "osteotomy", value: args as boolean });
			case "setVisibility_plate":
				return this._context.handle("setVisibility", { key: "plate", value: args as boolean });
			case "osteotomyCutClosing":
				return this._context.handle("osteotomyCut", OsteotomyCut.closing);
			case "osteotomyCutOpening":
				return this._context.handle("osteotomyCut", OsteotomyCut.opening);
			case "cutEoC":
				return this._context.handle("cutEoC", args as boolean);
			case "insertPlate":
				return this._context.handle("insertPlate", args as PlateModel);
			case "setFemurAxialRotation":
				return this._context.handle("setAxialRotation", { boneType: BoneTypeMinEnum.Femur, value: args as number } as SetAxialRotation); //todo BoneTypeMinEnum move
			case "setFemurAxialRotationSign":
				return this._context.handle("setAxialRotationSign", { boneType: BoneTypeMinEnum.Femur, value: args as AxialRotationEnum } as SetAxialRotationSign);
			case "setTibiaAxialRotation":
				return this._context.handle("setAxialRotation", { boneType: BoneTypeMinEnum.Tibia, value: args as number } as SetAxialRotation);
			case "setTibiaAxialRotationSign":
				return this._context.handle("setAxialRotationSign", { boneType: BoneTypeMinEnum.Tibia, value: args as AxialRotationEnum } as SetAxialRotationSign);
			case "startSelectingApex":
				return this._context.handle("startSelectingApex", null);
			case "cancelSelectingApex":
				return this._context.handle("cancelSelectingApex", null);
			case "switchToCalibrated":
				return this._context.handle("switchToCalibrated", null);
			case "switchToDeformity":
				return this._context.handle("switchToDeformity", null);
			case "switchToEOC":
				return this._context.handle("switchToEOC", null);
			case "switchToTemplating":
				return this._context.handle("switchToTemplating", null);
			case "switchToRPM":
				return this._context.handle("switchToRPM", null);
			case "startInsertingPlate":
				return this._context.handle("startInsertingPlate", null);
			case "confirmPlate":
				return this._context.handle("confirmPlate", null);
			case "cancelPlate":
				return this._context.handle("cancelPlate", null);
			case "flipPlate":
				return this._context.handle("flipPlate", args as PlateOrientationType);
			case "updateScrews":
				return this._context.handle("updateScrews", args as ScrewList);
			case "generateImagesToPrint":
				return this._context.handle("generateImagesToPrint", null);
			case "updateMeausuresOnlyPrint":
				return this._context.handle("updateMeausuresOnlyPrint", null);
			case "startBoneTypeSelection":
				return this._context.handle("startBoneTypeSelection", null);
			case "cancelBoneTypeSelection":
				return this._context.handle("cancelBoneTypeSelection", null);
			case "confirmBoneTypeSelection":
				return this._context.handle("confirmBoneTypeSelection", args as BoneTypeEnum);
		}

		return super.mapEvent(type, args);
	}
}
